import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button, Alert, Form, Row, Col } from "react-bootstrap";
import {
  FaRocket,
  FaShieldAlt,
  FaArrowLeft,
  FaInfoCircle,
  FaCrown,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { getDashboardInfo } from "../../../redux/slices/userSlice";
import { get, post } from "../../../utils/makeRequest";
import { showToast } from "../../../utils/toast";
import { getPlanPrice as getStaticPlanPrice } from "../../../utils/static";
import { countries } from "../../../utils/countries";
import {
  openCashfreeOrderCheckout,
  openCashfreeSubscriptionCheckout,
} from "../../../utils/cashfreeCheckout";
import PlanApiAccessList from "../../shared/PlanApiAccessList";
import { formatPlanApiCallsLabel } from "../../../utils/planApiAccess";
import {
  formatPlanDisplayPrice,
  roundPlanChargeAmount,
} from "../../../utils/planPricing";

const UpgradeModal = ({
  show,
  onHide,
  selectedPlan,
  billingCycle,
  currency,
  subscription,
  loading: parentLoading,
  onPayment,
  onCurrencyChange,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    billingName: "",
    billingAddress: "",
    additionalAddress: "",
    zipCode: "",
    city: "",
    state: "",
    country: "", // Change from "India" to empty string
    extraBillingInfo: "",
    gstNumber: "",
    vatNumber: "",
    phoneNumber: "",
  });

  // Auto-switch to billing form for paid plans
  useEffect(() => {
    if (show && selectedPlan) {
      // Skip billing form if custom plan already has billing details
      if (selectedPlan.isCustom && selectedPlan.billingDetails) {
        setShowBillingForm(false);
        setShowPricing(true);
        // Set billing details from the custom plan
        setBillingDetails({
          billingName: selectedPlan.billingDetails.billing_address || "",
          billingAddress: selectedPlan.billingDetails.billing_address || "",
          additionalAddress:
            selectedPlan.billingDetails.additional_address || "",
          zipCode: selectedPlan.billingDetails.zip_code || "",
          city: selectedPlan.billingDetails.city || "",
          state: selectedPlan.billingDetails.state || "",
          country: selectedPlan.billingDetails.country || "",
          extraBillingInfo:
            selectedPlan.billingDetails.extra_billing_info || "",
          gstNumber: selectedPlan.billingDetails.gst_number || "",
          vatNumber: selectedPlan.billingDetails.vat_number || "",
          phoneNumber: selectedPlan.billingDetails.phone_number || "",
        });
      } else {
        // Show billing form for ALL plans (including FREE)
        setShowBillingForm(true);
        setShowPricing(false);
      }
    }
  }, [show, selectedPlan]);

  // Prefill from saved billing address when modal opens.
  useEffect(() => {
    if (!show) return;
    (async () => {
      try {
        const resp = await get("/billing/get");
        const d = resp?.data;
        if (!resp?.success || !d) return;

        // Build a merged view first.
        const merged = {
          billingName: d.billing_name || "",
          billingAddress: d.billing_address || "",
          additionalAddress: d.additional_address || "",
          zipCode: d.zip_code || "",
          city: d.city || "",
          state: d.state || "",
          country: d.country || "",
          extraBillingInfo: d.extra_billing_info || "",
          gstNumber: d.gst_number || "",
          vatNumber: d.vat_number || "",
          phoneNumber: d.phone_number || "",
        };

        setBillingDetails((prev) => ({ ...prev, ...merged }));

        // If billing is already complete, skip the billing form and show pricing directly.
        const required = [
          merged.billingName,
          merged.billingAddress,
          merged.zipCode,
          merged.city,
          merged.country,
          merged.phoneNumber,
        ];
        const isComplete = required.every((v) => String(v || "").trim() !== "");
        if (isComplete) {
          setShowBillingForm(false);
          setShowPricing(true);
        } else {
          setShowBillingForm(true);
          setShowPricing(false);
        }
      } catch (_) {
        // silent: modal still works without prefill
      }
    })();
  }, [show]);

  // Remove the problematic useEffect that was causing the loop
  // Instead, handle currency switching only when country changes in the form

  // Updated: Helper function to get the correct price based on billing cycle with GST
  const getPlanPrice = (plan, cycle, curr) => {
    if (plan.calculatedPrice != null && !plan.isFree) {
      return roundPlanChargeAmount(plan.calculatedPrice, curr);
    }

    let basePrice = getStaticPlanPrice(plan, curr, cycle);

    if (curr === "INR" && billingDetails.country === "India") {
      return Math.round(basePrice * 1.18);
    }

    return basePrice;
  };

  const formatPrice = (price, currency) => {
    if (price === 0) return "Free";
    if (isNaN(price)) return "N/A";

    if (currency === "USD") {
      return `$${formatPlanDisplayPrice(price, "USD")}`;
    } else if (currency === "INR") {
      return `₹${formatPlanDisplayPrice(price, "INR")}`;
    }
    return `${price.toFixed(2)} ${currency}`;
  };

  // Use useMemo to calculate validation state without causing re-renders
  const isBillingFormValid = useMemo(() => {
    const required = [
      "billingName",
      "billingAddress",
      "zipCode",
      "city",
      "country",
      "phoneNumber",
    ];
    return required.every((field) => billingDetails[field].trim() !== "");
  }, [billingDetails]);

  // Handle billing form changes
  const handleBillingChange = (field, value) => {
    setBillingDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-switch to INR when India is selected
    if (field === "country" && value === "India" && onCurrencyChange) {
      onCurrencyChange("INR");
    }
  };

  // Handle country selection - switch currency based on country
  const handleCountrySelect = (e) => {
    const selectedValue = e.target.value;
    setBillingDetails((prev) => ({
      ...prev,
      country: selectedValue,
    }));

    // Switch currency based on country selection
    if (onCurrencyChange) {
      if (selectedValue === "India") {
        onCurrencyChange("INR");
      } else if (selectedValue !== "") {
        onCurrencyChange("USD");
      }
    }
  };

  // Handle billing form submission - move to pricing step
  const handleBillingSubmit = () => {
    if (isBillingFormValid) {
      setShowBillingForm(false);
      setShowPricing(true);
    }
  };

  // Handle back from pricing to billing
  const handleBackToBilling = () => {
    // If this is a custom plan with billing details, go back to calculator instead
    if (selectedPlan.isCustom && selectedPlan.billingDetails) {
      onHide(); // Close the UpgradeModal
      // The CustomPlanCalculator will handle showing the calculator again
    } else {
      setShowPricing(false);
      setShowBillingForm(true);
    }
  };

  // GST breakdown component - show only for India + INR
  const renderGSTBreakdown = () => {
    if (billingDetails.country !== "India" || currency !== "INR") return null;

    const breakdown = selectedPlan.priceBreakdown;
    if (breakdown) {
      return (
        <div className="mt-3 p-3 bg-light rounded">
          <h6 className="mb-2">Price Breakdown</h6>
          <div className="d-flex justify-content-between">
            <span>Subtotal (incl. extra calls):</span>
            <span>₹{breakdown.subtotalINR.toLocaleString()}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span>GST (18%):</span>
            <span>₹{breakdown.gstINR.toLocaleString()}</span>
          </div>
          <hr />
          <div className="d-flex justify-content-between fw-bold">
            <span>Total:</span>
            <span>₹{breakdown.totalINR.toLocaleString()}</span>
          </div>
        </div>
      );
    }

    const totalPrice = getPlanPrice(selectedPlan, billingCycle, currency);
    const gstAmount = Math.round((totalPrice * 0.18) / 1.18);

    return (
      <div className="mt-3 p-3 bg-light rounded">
        <h6 className="mb-2">Price Breakdown</h6>
        <div className="d-flex justify-content-between">
          <span>Base Price:</span>
          <span>₹{Math.round(totalPrice - gstAmount).toLocaleString()}</span>
        </div>
        <div className="d-flex justify-content-between">
          <span>GST (18%):</span>
          <span>₹{gstAmount.toLocaleString()}</span>
        </div>
        <hr />
        <div className="d-flex justify-content-between fw-bold">
          <span>Total:</span>
          <span>₹{totalPrice.toLocaleString()}</span>
        </div>
      </div>
    );
  };

  // Updated subscription handler
  const handleSubscribe = async () => {
    try {
      setLoading(true);

      // Persist billing details for future prefill + invoices.
      try {
        await post("/billing/save", billingDetails);
      } catch (e) {
        // Don't block payment flow, but surface warning.
        showToast.error("Unable to save billing info. Please try again.");
      }

      // For free plan, just activate (but now include billing details)
      if (selectedPlan.backendName === "FREE") {
        console.log(selectedPlan, "selectedPlan");
        const response = await post("/payment/activate-free-plan", {
          plan: selectedPlan.backendName,
          billingDetails: billingDetails, // Add billing details for free plan
        });

        if (response.success) {
          showToast.success("Free plan activated successfully!");
          dispatch(getDashboardInfo());
          onHide();
          // No full page reload; dashboard state refresh is enough.
        } else {
          throw new Error(response.error || "Failed to activate free plan");
        }
        setLoading(false);
        return;
      }

      // For paid plans, proceed with payment
      const interval = billingCycle === "yearly" ? "yearly" : "monthly";

      const response = await post("/payment/create-subscription", {
        plan: selectedPlan.backendName,
        interval: interval,
        currency: currency,
        billingDetails: billingDetails,
        customApiRequests: selectedPlan.customApiRequests,
        calculatedPrice: roundPlanChargeAmount(selectedPlan.calculatedPrice, currency),
        forwardRatesAddon: !!selectedPlan.forwardRatesAddon,
      });

      console.log(response, "responseee");

      if (
        response.paymentProvider === "cashfree" &&
        response.subscriptionSessionId
      ) {
        setPaymentInProgress(true);
        try {
          // Persist a pending activation flag so we can show an in-app
          // "verifying payment" message after redirect.
          try {
            const targetPlan = String(
              response?.newPlan || selectedPlan?.backendName || ""
            )
              .toUpperCase()
              .trim();
            localStorage.setItem(
              "pending_plan_activation",
              JSON.stringify({
                ts: Date.now(),
                targetPlan: targetPlan || null,
              })
            );
          } catch (_) {}

          showToast.info("Opening secure payment page…");
          await openCashfreeSubscriptionCheckout({
            subscriptionSessionId: response.subscriptionSessionId,
            cashfreeEnv: response.cashfreeEnv,
          });
        } finally {
          setPaymentInProgress(false);
        }
      } else if (
        response.paymentProvider === "cashfree_order" &&
        response.paymentSessionId
      ) {
        setPaymentInProgress(true);
        try {
          // Persist upfront payment context; MyPlan will verify + start mandate checkout after redirect.
          try {
            const targetPlan = String(
              response?.newPlan || selectedPlan?.backendName || ""
            )
              .toUpperCase()
              .trim();
            localStorage.setItem(
              "pending_upfront_activation",
              JSON.stringify({
                ts: Date.now(),
                targetPlan: targetPlan || null,
                orderId: response.orderId || null,
              })
            );
          } catch (_) {}

          await openCashfreeOrderCheckout({
            paymentSessionId: response.paymentSessionId,
            cashfreeEnv: response.cashfreeEnv,
          });
          showToast.success(
            "Complete payment on the secure page. Your custom plan activates after payment."
          );
        } finally {
          setPaymentInProgress(false);
        }
      } else {
        const errorMessage = response?.error || "Failed to create subscription";
        console.error("Subscription creation failed:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Subscription error:", error);
      const data = error?.response?.data;
      const message =
        (typeof data?.details === "string" && data.details) ||
        (typeof data?.hint === "string" && data.hint) ||
        (typeof data?.error === "string" && data.error) ||
        (typeof data?.message === "string" && data.message) ||
        error?.message ||
        "Failed to create subscription";
      showToast.error("Subscription failed: " + message);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedPlan) return null;

  const isCurrentlyLoading = loading || parentLoading || paymentInProgress;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaCrown className="me-2" />
          {selectedPlan.backendName === "FREE"
            ? "Activate Free Plan"
            : showBillingForm
            ? "Billing Information"
            : showPricing
            ? "Plan Pricing & Payment"
            : `Upgrade to ${selectedPlan?.displayName}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {showBillingForm ? (
          // Billing Form for ALL plans (including FREE)
          <div>
            <div className="alert alert-info mb-4">
              <FaInfoCircle className="me-2" />
              {selectedPlan.backendName === "FREE" 
                ? "Please provide your billing information. This is required for all plans, including the free plan."
                : "Please provide your billing information. This will be used to calculate the correct pricing and for invoicing."
              }
            </div>

            <Form>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Billing Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter billing name (e.g., Company Name, Personal Name)"
                      value={billingDetails.billingName}
                      onChange={(e) =>
                        handleBillingChange("billingName", e.target.value)
                      }
                      required
                      disabled={isCurrentlyLoading}
                    />
                    <Form.Text className="text-muted">
                      This name will appear on invoices and receipts
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Billing Address *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your full billing address"
                      value={billingDetails.billingAddress}
                      onChange={(e) =>
                        handleBillingChange("billingAddress", e.target.value)
                      }
                      required
                      disabled={isCurrentlyLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Additional Address (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Apartment, suite, unit, building, floor, etc."
                      value={billingDetails.additionalAddress}
                      onChange={(e) =>
                        handleBillingChange("additionalAddress", e.target.value)
                      }
                      disabled={isCurrentlyLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ZIP Code *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter ZIP/Postal code"
                      value={billingDetails.zipCode}
                      onChange={(e) =>
                        handleBillingChange("zipCode", e.target.value)
                      }
                      required
                      disabled={isCurrentlyLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter city"
                      value={billingDetails.city}
                      onChange={(e) =>
                        handleBillingChange("city", e.target.value)
                      }
                      required
                      disabled={isCurrentlyLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State/Province (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter state or province"
                      value={billingDetails.state}
                      onChange={(e) =>
                        handleBillingChange("state", e.target.value)
                      }
                      disabled={isCurrentlyLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country *</Form.Label>
                    <Form.Select
                      value={billingDetails.country}
                      onChange={handleCountrySelect}
                      required
                      disabled={isCurrentlyLoading}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number *</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter your phone number (e.g., +91 9876543210)"
                      value={billingDetails.phoneNumber}
                      onChange={(e) =>
                        handleBillingChange("phoneNumber", e.target.value)
                      }
                      required
                      disabled={isCurrentlyLoading}
                    />
                    <Form.Text className="text-muted">
                      This will be synced with your account profile
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>GST Number (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
                      value={billingDetails.gstNumber}
                      onChange={(e) =>
                        handleBillingChange("gstNumber", e.target.value)
                      }
                      disabled={isCurrentlyLoading}
                    />
                    <Form.Text className="text-muted">
                      For Indian businesses only
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>VAT Number (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter VAT number"
                      value={billingDetails.vatNumber}
                      onChange={(e) =>
                        handleBillingChange("vatNumber", e.target.value)
                      }
                      disabled={isCurrentlyLoading}
                    />
                    <Form.Text className="text-muted">
                      For EU/UK businesses only
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Additional Billing Information (Optional)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Any additional billing information or special instructions"
                      value={billingDetails.extraBillingInfo}
                      onChange={(e) =>
                        handleBillingChange("extraBillingInfo", e.target.value)
                      }
                      disabled={isCurrentlyLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
        ) : showPricing ? (
          // Pricing and Payment UI (including FREE plan confirmation)
          <div>
            {selectedPlan.backendName === "FREE" ? (
              // Free Plan Confirmation UI
              <div>
                <div className="alert alert-success mb-4">
                  <FaRocket className="me-2" />
                  <strong>Free Plan Activation</strong>
                  <br />
                  <small>
                    Get started with 100 API requests for one month, completely
                    free!
                  </small>
                </div>

                <div className="mb-4">
                  <PlanApiAccessList
                    planBackendName={selectedPlan.backendName}
                    apiCallsLabel={formatPlanApiCallsLabel(
                      selectedPlan.backendName,
                      500
                    )}
                    title="What's included"
                    titleClassName="h6 fw-bold mb-3"
                    listClassName="list mb-0"
                  />
                </div>

                <Alert variant="info">
                  <FaInfoCircle className="me-2" />
                  <strong>No Card Required:</strong> Free plan - no payment
                  needed. Your billing information has been saved for future use.
                </Alert>
              </div>
            ) : (
              // Paid Plan Pricing UI
              <div>
                <div className="text-center mb-4">
                  <div className="display-4 mb-3">
                    <FaRocket className="text-primary" />
                  </div>
                  <h3 className="fw-bold text-primary">
                    {selectedPlan.displayName}
                  </h3>
                  <p className="text-muted">{selectedPlan.description}</p>
                </div>

                {/* Show currency info based on country selection */}
                {billingDetails.country === "India" ? (
                  <div className="alert alert-info mb-4">
                    <FaInfoCircle className="me-2" />
                    <strong>Indian Pricing:</strong> Since you selected India as
                    your country, the pricing is shown in Indian Rupees (₹) with 18%
                    GST included.
                  </div>
                ) : billingDetails.country !== "" ? (
                  <div className="alert alert-warning mb-4">
                    <FaInfoCircle className="me-2" />
                    <strong>International Pricing:</strong> Since you selected a
                    country other than India, the pricing is shown in US Dollars ($)
                    without GST for international payment processing.
                  </div>
                ) : null}

                <div className="bg-light p-4 rounded mb-4">
                  <div className="row text-center">
                    <div className="col-md-6">
                      <h5 className="fw-bold text-success">
                        {formatPrice(
                          getPlanPrice(selectedPlan, billingCycle, currency),
                          currency
                        )}
                      </h5>
                      <small className="text-muted">
                        per {billingCycle === "yearly" ? "year" : "month"}
                        {billingDetails.country === "India" &&
                          currency === "INR" && (
                            <span className="text-warning d-block">
                              + GST included
                            </span>
                          )}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <h5 className="fw-bold text-info">
                        {selectedPlan.customApiRequests
                          ? selectedPlan.customApiRequests.toLocaleString()
                          : selectedPlan.apiLimit === "unlimited"
                            ? "Unlimited"
                            : selectedPlan.apiLimit?.toLocaleString()}
                      </h5>
                      <small className="text-muted">API Requests</small>
                    </div>
                  </div>
                </div>

                {/* GST Breakdown for India + INR only */}
                {renderGSTBreakdown()}

                <div className="mb-4">
                  <PlanApiAccessList
                    planBackendName={selectedPlan.backendName}
                    apiCallsLabel={formatPlanApiCallsLabel(
                      selectedPlan.backendName,
                      selectedPlan.customApiRequests ?? selectedPlan.apiLimit
                    )}
                    title="What's included"
                    titleClassName="h6 fw-bold mb-3"
                    listClassName="list mb-0"
                  />
                </div>

                <Alert variant="info">
                  <FaShieldAlt className="me-2" />
                  <strong>Secure Payment:</strong> Your payment is processed
                  securely through Cashfree. Your billing information will be saved
                  only after successful payment verification.
                </Alert>
              </div>
            )}
          </div>
        ) : (
          // This should not be shown anymore for paid plans
          <div>
            <div className="text-center mb-4">
              <div className="display-4 mb-3">
                <FaRocket className="text-primary" />
              </div>
              <h3 className="fw-bold text-primary">
                {selectedPlan.displayName}
              </h3>
              <p className="text-muted">{selectedPlan.description}</p>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {showBillingForm ? (
          // Billing Form Footer (for ALL plans including FREE)
          <div className="d-flex justify-content-between w-100">
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBillingSubmit}
              disabled={!isBillingFormValid || isCurrentlyLoading}
              size="lg"
              className="px-4 fw-bold"
            >
              {isCurrentlyLoading ? "Processing..." : 
                selectedPlan.backendName === "FREE" ? "Continue to Activation" : "Continue to Pricing"
              }
            </Button>
          </div>
        ) : showPricing ? (
          selectedPlan.backendName === "FREE" ? (
            // Free Plan Final Activation Footer
            <div className="d-flex justify-content-between w-100">
              <Button variant="secondary" onClick={handleBackToBilling}>
                <FaArrowLeft className="me-2" />
                Back to Billing
              </Button>
              <Button
                variant="success"
                onClick={handleSubscribe}
                disabled={loading}
                size="lg"
                className="px-4 fw-bold"
              >
                {loading ? "Activating..." : "Activate Free Plan"}
              </Button>
            </div>
          ) : (
            // Paid Plan Footer
            <div className="d-flex justify-content-between w-100">
              <Button variant="secondary" onClick={handleBackToBilling}>
                <FaArrowLeft className="me-2" />
                {selectedPlan.isCustom && selectedPlan.billingDetails
                  ? "Back to Calculator"
                  : "Back to Billing"}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubscribe}
                disabled={isCurrentlyLoading}
                size="lg"
                className="px-4 fw-bold"
              >
                {isCurrentlyLoading ? "Processing..." : "Subscribe Now"}
              </Button>
            </div>
          )
        ) : (
          // This should not be shown anymore
          <div className="d-flex justify-content-between w-100">
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowBillingForm(true)}
              size="lg"
              className="px-4 fw-bold"
            >
              Continue
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default UpgradeModal;
